import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import type {
  PaneResponsiveCardGridColumnMode,
  PaneResponsiveCardGridMaxColumns,
} from '@/components/layout/pane-responsive-card-grid-classes'
import {
  legacyColumnsToMaxColumns,
  resolveDashboardMetricGridClassName,
  type DashboardMetricColumnLayout,
  type DashboardMetricMaxColumns,
} from '@/components/dashboard/dashboard-metric-grid-classes'
import { resolveDashboardConfig } from '@/lib/resolve-dashboard-config'
import { CURRENT_CLIENT } from '@/constants'
import type { DashboardMetricVariant } from '@/components/dashboard/dashboard-metric-grid-context'
import { cn } from '@/lib/utils'

const defaultDashboardConfig = resolveDashboardConfig(CURRENT_CLIENT as { dashboard?: { showMetricIcons?: boolean } })

const headerClass = 'h-10 w-full rounded-xl'
const cardClass = 'h-24 w-full rounded-xl'

function DashboardMetricCardSkeleton({
  className,
  showIcons = defaultDashboardConfig.showMetricIcons,
  variant = 'card',
}: {
  className?: string
  showIcons?: boolean
  variant?: DashboardMetricVariant
}) {
  if (variant === 'plain') {
    return (
      <div className={cn('flex min-w-0 flex-col gap-1', className)}>
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-12" />
      </div>
    )
  }

  return (
    <Card className={cn('h-full', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        {showIcons ? <Skeleton className="h-4 w-4 shrink-0 rounded" /> : null}
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16" />
        <Skeleton className="mt-2 h-3 w-20" />
      </CardContent>
    </Card>
  )
}

/** Single dashboard stat placeholder — card-shaped silhouette. */
export function DashboardWidgetSkeleton({ className }: { className?: string }) {
  return <DashboardMetricCardSkeleton className={className} />
}

/** Row of metric card placeholders (e.g. application counts). */
export function DashboardStatsSkeleton({
  count,
  columns = 4,
  maxColumns,
  columnLayout = 'responsive',
  showIcons = defaultDashboardConfig.showMetricIcons,
  variant = 'card',
  layoutClassName,
}: {
  count: number
  /** @deprecated Prefer `maxColumns`. */
  columns?: 2 | 4
  maxColumns?: DashboardMetricMaxColumns
  columnLayout?: DashboardMetricColumnLayout
  showIcons?: boolean
  variant?: DashboardMetricVariant
  /** Replaces the built-in grid classes (tenant-specific ladders). */
  layoutClassName?: string
}) {
  const resolvedMaxColumns = maxColumns ?? legacyColumnsToMaxColumns(columns)

  const gridClassName = resolveDashboardMetricGridClassName({
    layoutClassName,
    columnLayout,
    itemCount: count,
    maxColumns: resolvedMaxColumns,
  })

  return (
    <div
      className={cn(gridClassName, 'items-stretch')}
      aria-busy="true"
      aria-label="Loading dashboard stats"
    >
      {Array.from({ length: count }).map((_, index) => (
        <DashboardMetricCardSkeleton key={index} showIcons={showIcons} variant={variant} />
      ))}
    </div>
  )
}

/** Section header + stacked or grid card placeholders. */
export function DashboardSectionSkeleton({
  cards = 2,
  grid = false,
  maxColumns,
  columnMode,
  className,
}: {
  cards?: number
  grid?: boolean
  maxColumns?: PaneResponsiveCardGridMaxColumns
  columnMode?: PaneResponsiveCardGridColumnMode
  className?: string
}) {
  return (
    <section className={cn('space-y-4', className)} aria-busy="true">
      <Skeleton className={headerClass} />
      {grid ? (
        <PaneResponsiveCardGrid maxColumns={maxColumns} columnMode={columnMode} className="items-start">
          {Array.from({ length: cards }).map((_, index) => (
            <Skeleton key={index} className={cardClass} />
          ))}
        </PaneResponsiveCardGrid>
      ) : (
        <div className="space-y-4">
          {Array.from({ length: cards }).map((_, index) => (
            <Skeleton key={index} className={cardClass} />
          ))}
        </div>
      )}
    </section>
  )
}
